import {
    chain, findIndex, get, has, includes, isEmpty, map, omit, reduce,
} from 'lodash';

import React, {
    useState,
    useEffect,
    useContext,
    useCallback, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import EditEmailTemplatesComponent from './EditEmailTemplatesComponent';
import EmailHttpService from '../../../api/EmailHttpService';
import ModalContext from '../../../contexts/ModalContext';

import ApplicationEventsContext, { EVENT_TYPES } from '../../../contexts/ApplicationEventsContext';
import NotificationContext from '../../../contexts/NotificationContext';
import LoadingContext, { LOADING_AREAS } from '../../../contexts/LoadingContext';
import Logger from '../../../services/logger';
import { templateTypes } from '../../../constants/emailTemplateTypes';

import {
    convertTextTemplateForEditor, convertTextTemplateForSystem,
} from '../email-editor/template-text-plugin/TemplateTextUtils';

import ConfirmationModal from '../../modals/ConfirmationModal';
import {
    SendgridStatusCodes,
    StatusCodes,
} from '../../../constants/APIResponseStatusConfig';
import TextService from '../../../services/TextService';
import { templateTranslatedNames } from '../../../constants/EmailTemplateNames';

const appEventForExternalSubjectChange = EVENT_TYPES.METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_SUBJECT;
const appEventForExternalBodyChange = EVENT_TYPES.METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_BODY;

// viewType need to make custom groups to display on ui, like Invitation and Registration,
const addViewType = (templates, types, title, order) => map(templates, (t) => (includes(types, t.type)
    ? {
        ...t,
        viewType: title,
        translatedType: title,
        order,
    }
    : {
        ...t,
        viewType: t.viewType || t.type,
    }));

const getViewTemplates = (templates, t) => {
    let viewTemplates = addViewType(templates, [
        templateTypes.inviteEmployerRep_BYTE,
        templateTypes.inviteEmployerRep_BYEMPREP,
        templateTypes.inviteEmployerRep,
        templateTypes.requestPasswordReset,
        templateTypes.deleteEmployerRep,
    ], t('EDITOR_TEMPLATE_GROUP_NAME_INVITATION_AND_REGISTRATION', 'Invitation and Registration Emails'), 1);

    viewTemplates = addViewType(viewTemplates, [
        templateTypes.creditsEmployer,
        templateTypes.creditUsageInPercentage,
    ], t('EDITOR_TEMPLATE_GROUP_NAME_CREDITS', 'Credit notifications'), 2);

    viewTemplates = addViewType(viewTemplates, [
        templateTypes.aiNotifications,
        templateTypes.completeJobDefinition,
        templateTypes.profileEmployer,
        templateTypes.publicPositionNotifications,
    ], t('EDITOR_TEMPLATE_GROUP_NAME_AI', 'AI notifications'), 4);

    viewTemplates = addViewType(viewTemplates, [
        templateTypes.atsIntegration,
        templateTypes.employerAtsIntegration,
    ], t('EDITOR_TEMPLATE_GROUP_NAME_ATS', 'ATS notifications'), 3);

    viewTemplates = addViewType(viewTemplates, [
        templateTypes.contactCandidateLinkedinInmail,
        templateTypes.positionFeedbackRequestToEmployerRep,
    ], t('EDITOR_TEMPLATE_GROUP_NAME_OTHER', 'Other Notifications'), 5);

    return chain(viewTemplates)
        .orderBy(['order', 'name'], ['asc', 'asc'])
        .map((template) => ({
            ...omit(template, ['template']),
            isMetadataTemplate: true,
            ...(template.template) && { body: template.template },
        }))
        .value();
};

// Modified by AI on 05/26/2026. Edit #1.
const EditEmailTemplatesContainer = ({
    onClose = () => {},
}) => {
    const {
        startFetchingData,
        stopFetchingData,
    } = useContext(LoadingContext);
    const { showErrorNotification, showSuccessNotification } = useContext(NotificationContext);
    const { showModal, hideModal } = useContext(ModalContext);
    const { dispatchEvent } = useContext(ApplicationEventsContext);

    const { t } = useTranslation();

    const [emailTemplates, setEmailTemplatesState] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({});

    const EmailTemplateFormValidationSchema = useMemo(() => ({
        subject: has(selectedTemplate, 'subject'),
        body: true,
    }), [selectedTemplate]);

    const invalidFieldNames = useMemo(() => reduce(EmailTemplateFormValidationSchema, (res, value, key) => {
        const isRequired = Boolean(value);
        const isValid = !isRequired || !isEmpty(formData[key]);
        return isValid ? res : [...res, key];
    }, []), [EmailTemplateFormValidationSchema, formData]);

    const textTemplateValues = useMemo(() => reduce(
        selectedTemplate?.tagNames || [],
        (result, current) => ({ ...result, [current]: current }),
        {},
    ), [selectedTemplate]);

    const buttonTemplateValues = useMemo(() => (selectedTemplate?.buttonNames || []), [selectedTemplate]);

    const fetchEmailTemplates = useCallback(async () => {
        try {
            startFetchingData(LOADING_AREAS.metadataEmailTemplatesTemplates);
            const responseTemplates = await EmailHttpService.getEmailTemplatesList();

            const resultTemplates = getViewTemplates(responseTemplates, t);

            const templateToSelect = get(resultTemplates, 0);

            setEmailTemplatesState(resultTemplates);
            onSelectedTemplateChange(templateToSelect);
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopFetchingData(LOADING_AREAS.metadataEmailTemplatesTemplates);
        }
    }, [t]);

    const onDescriptionChange = useCallback((value) => {
        setFormData((currentEmailData) => ({ ...currentEmailData, description: value }));
    }, []);

    const onSelectedTemplateChange = useCallback((template) => {
        const tagNamesToReplace = template?.tagNames;

        const convertedForEditorEmailSubject = template.subject
            ? TextService.sanitize( // prevent nested body
                convertTextTemplateForEditor(template.subject, tagNamesToReplace),
            )
            : '';

        const convertedForEditorEmailBody = TextService.sanitize(
            convertTextTemplateForEditor(template.body, tagNamesToReplace),
        );
        setSelectedTemplate(template);

        setFormData((currentEmailData) => ({
            ...currentEmailData,
            templateName: template.name,
            subject: convertedForEditorEmailSubject,
            body: convertedForEditorEmailBody,
            description: template.description || '',
        }));

        dispatchEvent({
            type: appEventForExternalSubjectChange,
            data: convertedForEditorEmailSubject,
        });

        dispatchEvent({
            type: appEventForExternalBodyChange,
            data: convertedForEditorEmailBody,
        });
    }, [selectedTemplate?.tagNames]);

    const saveEmailTemplate = useCallback(async () => {
        try {
            const updatedTemplate = {
                ...selectedTemplate,
                ...(formData?.body && { template: convertTextTemplateForSystem(formData.body) }),
                ...(formData?.subject && { subject: convertTextTemplateForSystem(formData.subject) }),
                ...(formData?.description && { description: formData.description }),
            };

            const selectedTemplateIndex = findIndex(emailTemplates, { uuid: selectedTemplate?.uuid });

            const responseTemplates = await EmailHttpService.updateEmailTemplate({ template: updatedTemplate });

            const resultTemplates = getViewTemplates(responseTemplates, t);

            setEmailTemplatesState(resultTemplates);
            onSelectedTemplateChange(get(resultTemplates, selectedTemplateIndex));
            showSuccessNotification(t('METADATA_EMAIL_TEMPLATE_UPDATED_NOTIFICATION_MESSAGE', 'Email Template Updated'));
        } catch (err) {
            Logger.error(err);
            showErrorNotification();
        }
    }, [selectedTemplate, formData, emailTemplates, t]);

    const onSaveEmailTemplate = useCallback(async () => {
        const modalId = showModal({
            body: <ConfirmationModal
                title={'SAVE_METADATA_EMAIL_TEMPLATE_CONFIRMATION_DIALOG_TITLE'}
                dynamicMessage={{ text: 'SAVE_METADATA_EMAIL_TEMPLATE_CONFIRMATION_DIALOG_TEXT' }}
                confirmButton={true}
                rejectButton={true}
                onRejectButtonClick={() => {
                    hideModal(modalId);
                }}
                onConfirmButtonClickAsync={async () => {
                    await saveEmailTemplate();
                    hideModal(modalId);
                }}
            />,
        });
    }, [saveEmailTemplate]);

    const sendTestMessage = useCallback(async () => {
        try {
            startFetchingData(LOADING_AREAS.metadataEmailTemplatesTemplatesTestMessage);
            const testMessageData = {
                ...(formData?.body && { template: formData.body }),
                ...(formData?.subject && { subject: formData.subject }),
            };

            const { statusCode } = await EmailHttpService.sendTestMessage(testMessageData);
            if (![StatusCodes.OK, SendgridStatusCodes.ACCEPTED].includes(statusCode)) {
                throw new Error('Can not send test message');
            }
            showSuccessNotification(t('EMAIL_TEMPLATE_TEST_MESSAGE_SENT_SUCCESS_NOTIFICATION', 'Test Message sent'));
        } catch (err) {
            showErrorNotification();
            Logger.error(err);
        } finally {
            stopFetchingData(LOADING_AREAS.metadataEmailTemplatesTemplatesTestMessage);
        }
    }, [formData, selectedTemplate]);

    useEffect(() => {
        fetchEmailTemplates();
    }, []);

    return (
        <EditEmailTemplatesComponent
            emailTemplates={emailTemplates}
            selectedTemplate={selectedTemplate}
            onSelectedTemplateChange={onSelectedTemplateChange}
            formData={formData}
            invalidFieldNames={invalidFieldNames}
            isValidForm={isEmpty(invalidFieldNames)}
            appEventForExternalSubjectChange={appEventForExternalSubjectChange}
            appEventForExternalBodyChange={appEventForExternalBodyChange}
            onEmailDataChange={setFormData}
            onClose={onClose}
            onDescriptionChange={onDescriptionChange}
            onSaveEmailTemplate={onSaveEmailTemplate}
            sendTestMessage={sendTestMessage}
            textTemplateValues={textTemplateValues}
            buttonTemplateValues={buttonTemplateValues}
        />
    );
};

EditEmailTemplatesContainer.propTypes = {
    onClose: PropTypes.func,
};

export default withRouter(EditEmailTemplatesContainer);
